import { PaletteOptions } from '@mui/material/styles';

import { ResolvedThemeMode, ThemePreference, resolveThemeMode } from './themePreference';

type PaletteVariant = {
  primary: string;
  pageBg: string;
  paper: string;
  surfaceSelected: string;
  leftMenuSelected: string;
  headerBg: string;
  headerShadow: string;
  textPrimary: string;
  textSecondary: string;
  formMain: string;
  formOutline: string;
  formDisabled: string;
  divider: string;
  error: string;
};

const darkPalette: PaletteVariant = {
  primary: '#33D0FF',
  pageBg: '#1F2429',
  paper: '#2A2F35',
  surfaceSelected: '#323840',
  leftMenuSelected: '#323840',
  headerBg: '#0E1114',
  headerShadow: '0 4px 8px rgba(0,0,0,0.5)',
  textPrimary: '#FFFFFF',
  textSecondary: 'rgba(255,255,255,0.7)',
  formMain: '#3A4149',
  formOutline: 'transparent',
  formDisabled: '#2F353B',
  divider: 'rgba(255,255,255,0.08)',
  error: '#FF6B6B',
};

const lightPalette: PaletteVariant = {
  primary: '#81CFDD',
  pageBg: '#FAFAFA',
  paper: '#FFFFFF',
  surfaceSelected: '#FFFFFF',
  leftMenuSelected: '#F3F3F3',
  headerBg: '#FFFFFF',
  headerShadow: '0 4px 5px rgba(0,0,0,0.08)',
  textPrimary: '#2F3A4A',
  textSecondary: '#626363',
  formMain: '#FFFFFF',
  formOutline: '#D6DEE8',
  formDisabled: '#F3F3F3',
  divider: '#E6EDF5',
  error: '#F52F2F',
};

const calmPalette: PaletteVariant = {
  ...lightPalette,
  primary: '#6FA4D9',
  pageBg: '#EDF3FB',
  surfaceSelected: '#EAF1FB',
  leftMenuSelected: '#EAF1FB',
  headerShadow: '0 4px 14px rgba(111,164,217,0.14)',
  formDisabled: '#E4EDF7',
  textSecondary: '#6C778C',
};

const naturePalette: PaletteVariant = {
  ...lightPalette,
  primary: '#4CAF7D',
  pageBg: '#F3F8F5',
  surfaceSelected: '#E8F3EC',
  leftMenuSelected: '#E8F3EC',
  headerShadow: '0 4px 14px rgba(76,175,125,0.12)',
  textSecondary: '#6F8576',
  formDisabled: '#E6F0E8',
  formOutline: '#D4E2D6',
  divider: '#E0E9E1',
};

const getVariant = (preference: ThemePreference): PaletteVariant => {
  const resolvedMode: ResolvedThemeMode = resolveThemeMode(preference);

  if (preference === 'calm') {
    return calmPalette;
  }

  if (preference === 'nature') {
    return naturePalette;
  }

  return resolvedMode === 'dark' ? darkPalette : lightPalette;
};

export const palette = (preference: ThemePreference): PaletteOptions => {
  const resolvedMode = resolveThemeMode(preference);
  const variant = getVariant(preference);

  return {
    mode: resolvedMode,
    primary: {
      main: variant.primary,
    },
    background: {
      default: variant.pageBg,
      paper: variant.paper,
    },
    text: {
      primary: variant.textPrimary,
      secondary: variant.textSecondary,
    },
    form: {
      main: variant.formMain,
      outline: variant.formOutline,
      disabled: variant.formDisabled,
    },
    divider: variant.divider,
    error: {
      main: variant.error,
    },
  };
};
